import { ReactElement, ReactNode, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'

const TooltipContainer = styled.div`
  align-items: center;
  display: inline-flex;
  justify-content: center;
  position: relative;
`

const TooltipChildren = styled.div`
  display: flex;
`

const setPosition = (props: { position?: TTooltipPosition }): string => {
  switch (props.position) {
    case 'top':
      return `
        transform: translate(-50%, -100%);
        top: -8px;
      `
    case 'bottom':
      return `
        transform: translate(-50%, 100%);
        bottom: -8px;
      `
    case 'left':
      return `
        transform: translate(-100%, -50%);
        left: -8px;
      `
    case 'right':
      return `
        transform: translate(100%, -50%);
        right: -8px;
      `
    default:
      return ``
  }
}

const setIndicator = (props: { position?: TTooltipPosition }): string => {
  switch (props.position) {
    case 'top':
      return `
        bottom: -10px;
        left: 50%;
        transform: translate(-50%, -100%) rotate(-180deg);
      `
    case 'bottom':
      return `
        top: -10px;
        left: 50%;
        transform: translate(-50%, 100%) rotate(0deg);
      `
    case 'left':
      return `
        right: 3px;
        top: 50%;
        transform: translate(100%, -50%) rotate(90deg) ;
      `
    case 'right':
      return `
        left: 3px;
        top: 50%;
        transform: translate(-100%, -50%) rotate(-90deg);
      `
    default:
      return ``
  }
}

const TooltipContent = styled.div<{ position?: TTooltipPosition }>`
  background-color: #f4ebff;
  border-radius: 5px;
  box-shadow: 8px 8px 16px rgba(102, 119, 150, 0.1);
  color: #393939;
  font-size: 12px;
  font-weight: 400;
  max-width: 400px;
  padding: 5px 10px;
  position: absolute;
  text-align: center;
  width: max-content;
  z-index: 10;

  ${setPosition}
`

const TooltipArrow = styled.div<{ position?: TTooltipPosition }>`
  content: '';
  position: absolute;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-bottom: 5px solid #f4ebff;
  clear: both;
  height: 0;
  width: 0;
  z-index: 10;

  ${setIndicator}
`

interface ITooltipPositionStyle {
  left?: number
  top?: number
  bottom?: number
  right?: number
}

export type TTooltipPosition = 'top' | 'bottom' | 'left' | 'right'

export interface ITooltipProps {
  className?: string
  position?: TTooltipPosition
  content: ReactNode
  children: ReactNode
  hideArrow?: boolean
  disabled?: boolean
}

const Tooltip = ({
  className,
  position = 'right',
  content,
  hideArrow = false,
  children,
  disabled
}: ITooltipProps): ReactElement => {
  const [open, setOpen] = useState(false)
  const childrenRef = useRef<HTMLDivElement>(null)
  const [positionStyle, setPositionStyle] = useState<ITooltipPositionStyle>({})

  const updatePositionStyle = (): void => {
    const dimensions = childrenRef.current?.getBoundingClientRect()
    if (dimensions) {
      let style = {}
      switch (position) {
        case 'top':
          style = {
            left: dimensions.left + dimensions.width / 2,
            top: dimensions.top
          }
          break
        case 'bottom':
          style = {
            left: dimensions.left + dimensions.width / 2,
            top: dimensions.bottom
          }
          break
        case 'left':
          style = {
            left: dimensions.left,
            top: dimensions.top + dimensions.height / 2
          }
          break
        case 'right':
          style = {
            left: dimensions.left + dimensions.width,
            top: dimensions.top + dimensions.height / 2
          }
          break
      }

      setPositionStyle(style)
    }
  }
  const handleMouseEnter = (): void => {
    if (!open) {
      setOpen(true)
      updatePositionStyle()
    }
  }

  const handleMouseLeave = (): void => {
    if (open) {
      setOpen(false)
      setPositionStyle({})
    }
  }

  return (
    <TooltipContainer>
      <TooltipChildren ref={childrenRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {children}
      </TooltipChildren>
      {createPortal(
        <div style={{ position: 'absolute', ...positionStyle }}>
          {open && !disabled && (
            <TooltipContent className={className} position={position}>
              <div>{content}</div>
              {!hideArrow && <TooltipArrow position={position} />}
            </TooltipContent>
          )}
        </div>,
        document.body
      )}
    </TooltipContainer>
  )
}

export default Tooltip
